import React, { useMemo, useState, useEffect } from 'react';
import useTranslate from '../../Hooks/useTranslate';
import { useAtom } from 'jotai';
import useAPI from '../../api';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Collapse,
  IconButton,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  CenteredFlexBox,
  FullSizeCenteredFlexBox,
} from '../../Components/FlexBox';
import { makeGenericFieldAtom } from '../../store/genericFormStore';
import { getFullPath } from './BasicField';
import { SchemaRenderer } from './SchemaRenderer';

export const StaticArrayField = ({ basePath, field }) => {
  const API = useAPI();
  const path = getFullPath(basePath, field.key);
  const translate = useTranslate();

  const fieldAtom = useMemo(() => makeGenericFieldAtom(path), [path]);
  const [items, setItems] = useAtom(fieldAtom);

  const [collapsed, setCollapsed] = useState(false);
  const [options, setOptions] = useState(field.options || []);
  const [loading, setLoading] = useState(false);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const hasFetchedRef = React.useRef(false);
  const lastFetchedUrlRef = React.useRef(null);

  useEffect(() => {
    if (field.optionsUrl && lastFetchedUrlRef.current !== field.optionsUrl) {
      lastFetchedUrlRef.current = field.optionsUrl;

      setLoading(true);

      API.lookupDynamicOptions(
        field.optionsUrl,
        (data) => {
          setOptions(Array.isArray(data) ? data : []);
          setLoading(false);
        },
        (err) => {
          console.error(`Failed to fetch options for ${field.key}`, err);
          setLoading(false);
        }
      );
    }
  }, [field.optionsUrl, field.key, API]);

  useEffect(() => {
    if (field.options) {
      setOptions(field.options);
    }
  }, [field.options]);

  useEffect(() => {
    if (options.length > 0) {
      let sourceData = [];
      if (Array.isArray(items) && items.length > 0) {
        sourceData = items;
      } else if (
        items &&
        typeof items === 'object' &&
        Object.keys(items).length > 0
      ) {
        sourceData = Object.values(items);
      } else if (
        Array.isArray(field.defaultValue) &&
        field.defaultValue.length > 0
      ) {
        sourceData = field.defaultValue;
      }

      const isStoreInSync =
        Array.isArray(items) &&
        items.length === options.length &&
        items.every((item, index) => {
          if (!item) return false;
          const opt = options[index];
          const optVal = opt.value !== undefined ? opt.value : opt.id;
          const optLab = opt.label !== undefined ? opt.label : opt.name;
          return item.optionValue === optVal && item.optionLabel === optLab;
        });

      if (isStoreInSync) return;

      const mergedItems = options.map((opt, index) => {
        const optVal = opt.value !== undefined ? opt.value : opt.id;
        const optLab = opt.label !== undefined ? opt.label : opt.name;

        const existingData =
          sourceData.find((item) => item?.optionValue === optVal) ||
          sourceData[index] ||
          {};

        const defaults = (field.itemSchema || []).reduce((acc, subField) => {
          acc[subField.key] = subField.defaultValue ?? '';
          return acc;
        }, {});

        return {
          ...defaults,
          ...existingData,
          optionValue: optVal,
          optionLabel: optLab,
        };
      });

      setItems(mergedItems, { isInit: true });
    }
  }, [options, items, field.itemSchema, field.defaultValue, setItems]);

  if (loading) {
    return (
      <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          {translate('LOADING')} {translate(field.label)}...
        </Typography>
      </Box>
    );
  }

  const safeRenderItems = Array.isArray(items)
    ? items
    : items && typeof items === 'object'
      ? Object.values(items)
      : [];

  return (
    <Paper variant="outlined" sx={{ p: '16px' }}>
      <CenteredFlexBox justifyContent="space-between">
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {translate(field.label)}
        </Typography>
        <IconButton id="toggle-paper" onClick={toggleCollapsed}>
          <KeyboardArrowDownRoundedIcon
            sx={{
              transform: !collapsed ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s ease',
            }}
          />
        </IconButton>
      </CenteredFlexBox>
      <Collapse in={!collapsed}>
        <Grid container columnSpacing={3} rowSpacing={1}>
          {safeRenderItems.map((item, index) => (
            <Grid key={index} size={6}>
              <FullSizeCenteredFlexBox
                justifyContent="space-between"
                gap="16px"
              >
                {item?.optionLabel && (
                  <Typography
                    variant="body1"
                    sx={{ minWidth: '150px', fontWeight: 'bold', flexGrow: 1 }}
                  >
                    {translate(item.optionLabel)}
                  </Typography>
                )}

                <SchemaRenderer
                  fields={field.itemSchema}
                  basePath={`${path}.${index}`}
                />
              </FullSizeCenteredFlexBox>
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Paper>
  );
};
